import type React from 'react';
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { getPersistedFilters, setPersistedFilters } from '@/lib/session-storage-filters';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  HandCoins,
  Search,
  Mail,
  Phone,
  X,
  AlertCircle,
  CheckCircle2,
  Clock,
  TrendingUp,
  Settings,
  Info,
  Loader2,
  UserCog,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ArrowDownToLine,
  Filter,
  Plus,
  MoreVertical,
  Pin,
  Maximize2,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import type { PromiseToPay } from '@/types/collections';
import {
  usePromisesPageData,
  useBulkReassignPromises,
  useBulkUpdatePromiseStatus,
  buildGraphQLFilterVariable,
  type GetPromisesToPayParams,
} from '@/hooks/usePromisesToPayData';
import { useCompanyUsersList } from '@/hooks/useUsersData';
import { useCurrenciesList } from '@/hooks/useCurrenciesData';
import { useCompany } from '@/hooks/use-company';
import { Checkbox } from '@/components/ui/checkbox';
import { ColumnConfigDialog, type ColumnDefinition } from '@/components/shared/column-config-dialog';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';

import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, formatDate, formatDateDDMMYYYY } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils/formatters';
import { getPageNumbers } from '@/lib/pagination-utils';
import { HelpSupportButton } from '@/components/help-support';
import { LogCallModal, ReassignUserModal, UpdateStatusModal } from '@/components/collections/action-modals';
import { PromiseSendEmailModal } from '@/components/collections/promise-send-email-modal';
import { useExportAll } from '@/hooks/useExportAll';
import { ExportProvider } from '@/providers/ExportProvider';
import type { ColumnInput } from '@/types/export';
import { CurrencyToggle, type CurrencyMode } from '@/components/shared/currency-toggle';
import { LastActionCell } from '@/components/collections/last-action-cell';

const DEFAULT_PROMISE_COLUMNS: ColumnDefinition[] = [
  { id: 'select', label: 'Select', visible: true, order: 0 },
  { id: 'promiseId', label: 'Promise ID', visible: true, order: 1 },
  { id: 'customerName', label: 'Customer', visible: true, order: 2 },
  { id: 'caseNumber', label: 'Case Number', visible: true, order: 3 },
  { id: 'lastAction', label: 'Last Action', visible: true, order: 4 },
  { id: 'promiseDate', label: 'Promise Date', visible: true, order: 5 },
  { id: 'daysOverdue', label: 'Days Overdue', visible: true, order: 6 },
  { id: 'currency', label: 'Currency', visible: true, order: 7 },
  { id: 'amount', label: 'Amount', visible: true, order: 8 },
  { id: 'transactions', label: 'Transactions', visible: true, order: 9 },
  { id: 'status', label: 'Status', visible: true, order: 10 },
  { id: 'method', label: 'Method', visible: true, order: 11 },
  { id: 'customerAccountNumber', label: 'Account Number', visible: false, order: 12 },
  { id: 'customerEmail', label: 'Email', visible: false, order: 13 },
  { id: 'customerPhone', label: 'Phone', visible: false, order: 14 },
  { id: 'createdDate', label: 'Created Date', visible: false, order: 15 },
  { id: 'createdBy', label: 'Created By', visible: false, order: 16 },
  { id: 'assignedUser', label: 'Assigned To', visible: false, order: 17 },
  { id: 'notes', label: 'Notes', visible: false, order: 18 },
];

// Map frontend sort field names to backend sort field names
const sortFieldToBackend: Record<string, string> = {
  promiseId: 'PromiseId',
  customerName: 'CustomerName',
  caseNumber: 'CaseNumber',
  promiseDate: 'PromiseDate',
  daysOverdue: 'DaysOverdue',
  currency: 'CurrencyCode',
  amount: 'PromisedAmount',
  transactions: 'InvoiceCount',
  status: 'StatusName',
  method: 'ConfirmationMethodName',
  customerAccountNumber: 'CustomerAccountNumber',
  customerEmail: 'ContactEmail',
  customerPhone: 'ContactPhone',
  createdDate: 'CreatedAt',
  createdBy: 'CreatedByName',
  assignedUser: 'AssignedUserName',
  notes: 'Notes',
};

const SORTABLE_COLUMNS: string[] = [
  'promiseId',
  'customerName',
  'caseNumber',
  'promiseDate',
  'daysOverdue',
  'currency',
  'amount',
  'transactions',
  'status',
  'method',
  'customerAccountNumber',
  'customerEmail',
  'customerPhone',
  'createdDate',
  'createdBy',
  'assignedUser',
  'notes',
];

const COLUMN_HEADERS: Record<string, { label: string; className?: string }> = {
  promiseId: { label: 'Promise ID' },
  customerName: { label: 'Customer' },
  caseNumber: { label: 'Case Number' },
  lastAction: { label: 'Last Action' },
  promiseDate: { label: 'Promise Date' },
  daysOverdue: { label: 'Days Overdue' },
  currency: { label: 'Currency' },
  amount: { label: 'Amount', className: 'text-right' },
  transactions: { label: 'Transactions' },
  status: { label: 'Status' },
  method: { label: 'Method' },
  customerAccountNumber: { label: 'Account Number' },
  customerEmail: { label: 'Email' },
  customerPhone: { label: 'Phone' },
  createdDate: { label: 'Created Date' },
  createdBy: { label: 'Created By' },
  assignedUser: { label: 'Assigned To' },
  notes: { label: 'Notes' },
};

type SortField =
  | 'promiseId'
  | 'customerName'
  | 'caseNumber'
  | 'promiseDate'
  | 'daysOverdue'
  | 'currency'
  | 'amount'
  | 'transactions'
  | 'status'
  | 'method'
  | 'customerAccountNumber'
  | 'customerEmail'
  | 'customerPhone'
  | 'createdDate'
  | 'createdBy'
  | 'assignedUser'
  | 'notes';
type SortDirection = 'asc' | 'desc' | null;

const STATUS_OPTIONS = ['Active', 'Broken', 'Paid', 'Partially Paid', 'Closed'] as const;

interface AdvancedFilters {
  statuses: string[];
  assignedUser: string;
  currencies: string[];
  dueToday: boolean;
  createdAtFrom: string;
  createdAtTo: string;
}

const DEFAULT_ADVANCED_FILTERS: AdvancedFilters = {
  statuses: ['Active', 'Broken', 'Partially Paid'],
  assignedUser: 'all',
  currencies: [],
  dueToday: false,
  createdAtFrom: '',
  createdAtTo: '',
};

interface PromiseKPICardProps {
  title: string;
  value: number;
  subtitle: string;
  icon: React.FC<{ className?: string }>;
  colorScheme: 'green' | 'red' | 'amber';
  isLoading: boolean;
  activeCurrencyFilter: string;
  baseCurrency: string;
}

const colorMap = {
  green: {
    border: 'border-l-green-500',
    bg: 'bg-gradient-to-br from-green-50/30 to-transparent dark:from-green-950/20',
    iconBg: 'bg-green-100',
    iconText: 'text-green-600',
  },
  red: {
    border: 'border-l-red-500',
    bg: 'bg-gradient-to-br from-red-50/30 to-transparent dark:from-red-950/20',
    iconBg: 'bg-red-100',
    iconText: 'text-red-600',
  },
  amber: {
    border: 'border-l-amber-500',
    bg: 'bg-gradient-to-br from-amber-50/30 to-transparent dark:from-amber-950/20',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
  },
} as const;

const PromiseKPICard: React.FC<PromiseKPICardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  colorScheme,
  isLoading,
  activeCurrencyFilter,
  baseCurrency,
}) => {
  const colors = colorMap[colorScheme];
  const tooltipText =
    activeCurrencyFilter !== baseCurrency
      ? `Total for ${activeCurrencyFilter} promises only. Values shown in ${activeCurrencyFilter}.`
      : `Total shown in base currency (${baseCurrency}). Includes all promises.`;

  return (
    <Card className={`transition-all border-l-4 ${colors.border} ${colors.bg}`}>
      <CardContent className="pt-6">
        <div className="relative">
          <div className="absolute top-0 right-0">
            <div className={`p-2 rounded-full ${colors.iconBg} flex-shrink-0`}>
              <Icon className={`h-4 w-4 ${colors.iconText}`} />
            </div>
          </div>
          <div className="flex items-center gap-1">
            <p className="text-sm font-medium">{title}</p>
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info
                    className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0"
                    aria-label={`${title} info`}
                  />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[260px] text-center">
                  {tooltipText}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {isLoading ? (
            <div className="mt-1 h-8 w-24 animate-pulse rounded bg-muted" />
          ) : (
            <div className="text-2xl font-bold mt-1">{value}</div>
          )}
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export const PROMISES_LIST_FILTERS_KEY = 'promises-list-filters';

interface PersistedPromisesFilters {
  advancedFilters: AdvancedFilters;
  searchQuery: string;
}

const PromisesPageWrapper: React.FC = () => (
  <ExportProvider>
    <PromisesPage />
  </ExportProvider>
);

export default PromisesPageWrapper;

const PromisesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const persistedFilters = useMemo(() => getPersistedFilters<PersistedPromisesFilters>(PROMISES_LIST_FILTERS_KEY), []);
  const [searchQuery, setSearchQuery] = useState(persistedFilters?.searchQuery ?? '');
  const [selectedPromiseForModal, setSelectedPromiseForModal] = useState<PromiseToPay | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [reassignUserModalOpen, setReassignUserModalOpen] = useState(false);
  const [updateStatusModalOpen, setUpdateStatusModalOpen] = useState(false);
  const [selectedPromiseIds, setSelectedPromiseIds] = useState<Set<string>>(new Set());
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [currencyMode, setCurrencyMode] = useState<CurrencyMode>('customer');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Sorting state
  const [sortField, setSortField] = useState<SortField | null>('promiseDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Derive initial filters from URL search params (e.g. ?statuses=Active,Broken&assignedUser=123&createdAtFrom=2026-01-01&createdAtTo=2026-03-31)
  const initialFilters = useMemo((): AdvancedFilters => {
    const statusesParam = searchParams.get('statuses');
    const assignedUserParam = searchParams.get('assignedUser');
    const createdAtFromParam = searchParams.get('createdAtFrom');
    const createdAtToParam = searchParams.get('createdAtTo');
    const currenciesParam = searchParams.get('currencies');
    return {
      statuses: statusesParam ? statusesParam.split(',').filter(Boolean) : DEFAULT_ADVANCED_FILTERS.statuses,
      assignedUser: assignedUserParam || 'all',
      currencies: currenciesParam ? currenciesParam.split(',').filter(Boolean) : [],
      dueToday: false,
      createdAtFrom: createdAtFromParam || '',
      createdAtTo: createdAtToParam || '',
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- read URL params only on mount

  // Filter drawer state (matches Collection Queue pattern)
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(
    persistedFilters?.advancedFilters ?? initialFilters,
  );
  const [draftFilters, setDraftFilters] = useState<AdvancedFilters>(
    persistedFilters?.advancedFilters ?? initialFilters,
  );

  const handleOpenMoreFilters = useCallback(() => {
    setDraftFilters({ ...advancedFilters });
    setMoreFiltersOpen(true);
  }, [advancedFilters]);

  const handleApplyFilters = useCallback(() => {
    setAdvancedFilters({ ...draftFilters });
    setMoreFiltersOpen(false);
    setCurrentPage(1);
  }, [draftFilters]);

  const handleClearDraftFilters = useCallback(() => {
    setDraftFilters({ ...DEFAULT_ADVANCED_FILTERS });
  }, []);

  const handleCloseMoreFilters = useCallback((open: boolean) => {
    if (!open) {
      setMoreFiltersOpen(false);
    }
  }, []);

  // Persist filter state to sessionStorage on change
  useEffect(() => {
    const state: PersistedPromisesFilters = { advancedFilters, searchQuery };
    setPersistedFilters(PROMISES_LIST_FILTERS_KEY, state);
  }, [advancedFilters, searchQuery]);

  // Count active filters for badge display
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (JSON.stringify(advancedFilters.statuses) !== JSON.stringify(DEFAULT_ADVANCED_FILTERS.statuses)) count += 1;
    if (advancedFilters.assignedUser !== 'all') count += 1;
    if (advancedFilters.currencies.length > 0) count += 1;
    if (advancedFilters.dueToday) count += 1;
    if (advancedFilters.createdAtFrom || advancedFilters.createdAtTo) count += 1;
    return count;
  }, [advancedFilters]);

  // Column preferences
  const {
    columns,
    visibleColumns: baseVisibleColumnDefs,
    savePreferences,
    resetToDefaults,
    columnOrder,
    setColumnOrder,
    columnWidths,
    setColumnWidths,
    pinnedLeft,
    pinnedRight,
    setPinned,
    resetColumnOrder,
    resetColumnWidths,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.PromiseToPay, DEFAULT_PROMISE_COLUMNS);
  const baseVisibleColumns = baseVisibleColumnDefs.map((col) => col.id);
  const visibleColumns =
    columnOrder.length > 0
      ? columnOrder
          .filter((id) => baseVisibleColumns.includes(id))
          .concat(baseVisibleColumns.filter((id) => !columnOrder.includes(id)))
      : baseVisibleColumns;

  // Column resize state
  const resizingRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);
  const [localColumnWidths, setLocalColumnWidths] = useState<Record<string, number>>(columnWidths);
  const localColumnWidthsRef = useRef(localColumnWidths);
  localColumnWidthsRef.current = localColumnWidths;

  useEffect(() => {
    setLocalColumnWidths(columnWidths);
  }, [columnWidths]);

  const handleResizeMouseDown = (e: React.MouseEvent, columnId: string, currentWidth: number) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { columnId, startX: e.clientX, startWidth: currentWidth };
    const handleMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const { columnId: colId, startX, startWidth } = resizingRef.current;
      const delta = ev.clientX - startX;
      const newWidth = Math.max(60, startWidth + delta);
      setLocalColumnWidths((prev) => ({ ...prev, [colId]: newWidth }));
    };
    const handleMouseUp = () => {
      if (resizingRef.current) {
        setColumnWidths(localColumnWidthsRef.current);
      }
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Column reorder state
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const handlePinLeft = (columnId: string) => {
    const newPinnedLeft = pinnedLeft.includes(columnId)
      ? pinnedLeft.filter((id) => id !== columnId)
      : [...pinnedLeft, columnId];
    const newPinnedRight = pinnedRight.filter((id) => id !== columnId);
    setPinned(newPinnedLeft, newPinnedRight);
  };

  const handlePinRight = (columnId: string) => {
    const newPinnedRight = pinnedRight.includes(columnId)
      ? pinnedRight.filter((id) => id !== columnId)
      : [...pinnedRight, columnId];
    const newPinnedLeft = pinnedLeft.filter((id) => id !== columnId);
    setPinned(newPinnedLeft, newPinnedRight);
  };

  const handleResetColumnWidths = () => resetColumnWidths();
  const handleResetColumnOrder = () => resetColumnOrder();

  // Build server-side query params from current filter/sort/page state
  const queryParams = useMemo((): GetPromisesToPayParams => {
    const params: GetPromisesToPayParams = {
      page: currentPage,
      pageSize: itemsPerPage,
    };

    if (searchQuery) {
      params.search = searchQuery;
    }

    // Status filter from advanced filters (OR logic: Active OR Broken etc.)
    if (advancedFilters.statuses.length > 0) {
      params.statuses = advancedFilters.statuses;
    }

    // Due Today filter
    if (advancedFilters.dueToday) {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      params.dateFrom = todayStr;
      params.dateTo = todayStr;
    }

    // User filter
    if (advancedFilters.assignedUser !== 'all') {
      params.assignedUserId = Number(advancedFilters.assignedUser);
    }

    // Currency filter
    if (advancedFilters.currencies.length > 0) {
      params.currencyCodes = advancedFilters.currencies;
    }

    // Created At date range filter
    if (advancedFilters.createdAtFrom) {
      params.createdAtFrom = advancedFilters.createdAtFrom;
    }
    if (advancedFilters.createdAtTo) {
      params.createdAtTo = advancedFilters.createdAtTo;
    }

    // Sorting
    if (sortField && sortDirection) {
      params.sortBy = sortFieldToBackend[sortField] ?? 'PromiseDate';
      params.sortOrder = sortDirection === 'desc' ? 'desc' : 'asc';
    }

    return params;
  }, [currentPage, itemsPerPage, searchQuery, advancedFilters, sortField, sortDirection]);

  // Fetch data from server with all params
  const useBaseAmount = currencyMode === 'base' || advancedFilters.currencies.length !== 1;
  const { promises, pagination, kpis, isLoading } = usePromisesPageData(queryParams, useBaseAmount);

  // Derive pagination values from server response
  const totalPages = pagination.totalPages;
  const pageNumbers = getPageNumbers(currentPage, totalPages);

  // User filter options
  const { data: usersList, isLoading: isLoadingUsers } = useCompanyUsersList();

  // Currency filter options
  const { data: currencies } = useCurrenciesList();
  const { selectedCompany } = useCompany();
  const baseCurrency = selectedCompany?.currency || 'GBP';
  const currencyFilterOptions = useMemo(() => {
    return (currencies ?? []).map((x) => ({
      value: x.code,
      label: `${x.code} - ${x.name}`,
    }));
  }, [currencies]);

  const activeCurrencyFilter = useMemo(() => {
    if (currencyMode === 'base') return baseCurrency;
    if (advancedFilters.currencies.length === 1) return advancedFilters.currencies[0];
    return baseCurrency;
  }, [currencyMode, baseCurrency, advancedFilters.currencies]);

  const userFilterOptions = useMemo(() => {
    if (!usersList) return [];
    return usersList
      .map((x) => ({
        id: String(x.id),
        name: `${x.firstName} ${x.lastName}`.trim(),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [usersList]);

  // Sorting handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField('promiseDate');
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="ml-2 h-4 w-4" />;
    if (sortDirection === 'asc') return <ArrowUp className="ml-2 h-4 w-4" />;
    return <ArrowDown className="ml-2 h-4 w-4" />;
  };

  const handleViewDetails = (promise: PromiseToPay) => {
    navigate(`/collections/promises/${promise.id}`);
  };

  const handleEmailCustomer = (promise: PromiseToPay) => {
    setSelectedPromiseForModal(promise);
    setEmailModalOpen(true);
  };

  const handleLogCall = (promise: PromiseToPay) => {
    setSelectedPromiseForModal(promise);
    setCallModalOpen(true);
  };

  const handleCaseClick = (caseId: string) => {
    navigate(`/collections/queue/case/${caseId}`, {
      state: { from: 'promises' },
    });
  };

  const getStatusBadge = (status: PromiseToPay['status']) => {
    const variants: Record<
      PromiseToPay['status'],
      { variant: 'default' | 'destructive' | 'secondary' | 'outline'; className: string }
    > = {
      Active: { variant: 'default', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
      Broken: { variant: 'destructive', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
      Paid: { variant: 'default', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
      'Partially Paid': {
        variant: 'secondary',
        className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
      },
      Closed: { variant: 'outline', className: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
    };
    return <Badge className={variants[status].className}>{status}</Badge>;
  };

  const getConfirmationMethodBadge = (method: PromiseToPay['confirmationMethod']) => {
    const icons = {
      Phone: <Phone className="h-3 w-3 mr-1" />,
      Email: <Mail className="h-3 w-3 mr-1" />,
      Portal: <TrendingUp className="h-3 w-3 mr-1" />,
    };
    return (
      <Badge variant="outline" className="flex items-center gap-1">
        {icons[method]}
        {method}
      </Badge>
    );
  };

  const handleCreateNewPromise = () => {
    navigate('/collections/promises/create');
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedPromiseIds(new Set(promises.map((x) => x.id)));
    } else {
      setSelectedPromiseIds(new Set());
    }
  };

  const handleSelectPromise = (promiseId: string, checked: boolean) => {
    const newSelection = new Set(selectedPromiseIds);
    if (checked) {
      newSelection.add(promiseId);
    } else {
      newSelection.delete(promiseId);
    }
    setSelectedPromiseIds(newSelection);
  };

  const isAllSelected = promises.length > 0 && selectedPromiseIds.size === promises.length;
  const isSomeSelected = selectedPromiseIds.size > 0 && selectedPromiseIds.size < promises.length;

  // Bulk action handlers
  const handleBulkEmail = () => {
    if (selectedPromiseIds.size === 1) {
      const promiseId = Array.from(selectedPromiseIds)[0];
      const promise = promises.find((x) => x.id === promiseId);
      if (promise) handleEmailCustomer(promise);
    } else {
      toast.success(`Email sent to ${selectedPromiseIds.size} customers`);
      setSelectedPromiseIds(new Set());
    }
  };

  const handleBulkCall = () => {
    if (selectedPromiseIds.size === 1) {
      const promiseId = Array.from(selectedPromiseIds)[0];
      const promise = promises.find((x) => x.id === promiseId);
      if (promise) handleLogCall(promise);
    } else {
      toast.info(`Logging calls for ${selectedPromiseIds.size} customers`);
    }
  };

  const handleClearSelection = () => setSelectedPromiseIds(new Set());
  const handleBulkReassignUser = () => setReassignUserModalOpen(true);
  const handleBulkUpdateStatus = () => setUpdateStatusModalOpen(true);

  const bulkReassignMutation = useBulkReassignPromises();
  const bulkUpdateStatusMutation = useBulkUpdatePromiseStatus();

  const reassignUserOptions = useMemo(() => {
    if (!usersList) return [];
    return usersList.map((x) => ({
      id: String(x.id),
      name: `${x.firstName} ${x.lastName}`.trim(),
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(`${x.firstName} ${x.lastName}`.trim())}&background=random&size=128`,
    }));
  }, [usersList]);

  const handleReassignUserSave = (userId: string, notes: string) => {
    const promiseIds = Array.from(selectedPromiseIds).map(Number);
    bulkReassignMutation.mutate(
      { promiseIds, assignedUserId: Number(userId), notes: notes || undefined },
      {
        onSuccess: () => setSelectedPromiseIds(new Set()),
        onError: (error) => toast.error(`Failed to reassign: ${error.message}`),
      },
    );
  };

  const handleUpdateStatusSave = (status: string, notes: string) => {
    const promiseIds = Array.from(selectedPromiseIds).map(Number);
    bulkUpdateStatusMutation.mutate(
      { promiseIds, status, notes: notes || undefined },
      {
        onSuccess: () => setSelectedPromiseIds(new Set()),
        onError: (error) => toast.error(`Failed to update status: ${error.message}`),
      },
    );
  };

  // Map frontend column IDs to backend PromiseToPayList property names for export
  const exportColumnNameMap: Record<string, string> = useMemo(
    () => ({
      promiseId: 'PromiseId',
      customerName: 'CustomerName',
      customerAccountNumber: 'CustomerAccountNumber',
      caseNumber: 'CaseNumber',
      lastAction: 'LastActionDescription',
      promiseDate: 'PromiseDate',
      daysOverdue: 'DaysOverdue',
      currency: 'CurrencyCode',
      amount: 'PromisedAmount',
      transactions: 'InvoiceCount',
      status: 'StatusName',
      method: 'ConfirmationMethodName',
      customerEmail: 'ContactEmail',
      customerPhone: 'ContactPhone',
      createdDate: 'CreatedAt',
      createdBy: 'CreatedByName',
      assignedUser: 'AssignedUserName',
      notes: 'Notes',
    }),
    [],
  );

  // Server-side export
  const getExportColumns = useCallback((): ColumnInput[] => {
    return baseVisibleColumnDefs
      .filter((col) => col.id in exportColumnNameMap)
      .map((col) => ({ name: exportColumnNameMap[col.id], displayName: col.label }));
  }, [baseVisibleColumnDefs, exportColumnNameMap]);

  const getAllExportColumns = useCallback((): ColumnInput[] => {
    return DEFAULT_PROMISE_COLUMNS.filter((col) => col.id in exportColumnNameMap).map((col) => ({
      name: exportColumnNameMap[col.id],
      displayName: col.label,
    }));
  }, [exportColumnNameMap]);

  const getExportFilter = useCallback(
    (): Record<string, unknown> | undefined => buildGraphQLFilterVariable(queryParams),
    [queryParams],
  );

  const { triggerExportAll: triggerExportCurrentView } = useExportAll({
    form: 'PromisesToPay',
    getColumns: getExportColumns,
    getFilter: getExportFilter,
    fileName: `promises-export-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerExportAllColumns } = useExportAll({
    form: 'PromisesToPay',
    getColumns: getAllExportColumns,
    getFilter: getExportFilter,
    fileName: `promises-all-columns-export-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const handleExportToExcel = () => {
    triggerExportCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  const handleExportAllColumns = () => {
    triggerExportAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  // Render cell content for a column
  const renderCellContent = (colId: string, promise: PromiseToPay): React.ReactNode => {
    switch (colId) {
      case 'promiseId':
        return (
          <button
            onClick={() => handleViewDetails(promise)}
            className="text-primary hover:underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded cursor-pointer transition-colors"
          >
            {promise.id}
          </button>
        );
      case 'customerName':
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="font-medium truncate max-w-[200px] block">{promise.customerName}</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{promise.customerName}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      case 'customerAccountNumber':
        return promise.customerAccountNumber;
      case 'caseNumber':
        return promise.caseNumber ? (
          <button
            onClick={() => handleCaseClick(promise.caseId)}
            className="text-primary hover:underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded cursor-pointer transition-colors font-medium"
          >
            {promise.caseNumber}
          </button>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'lastAction':
        return <LastActionCell lastAction={promise.lastAction} />;
      case 'promiseDate':
        return formatDateDDMMYYYY(promise.promiseDate.toISOString());
      case 'daysOverdue':
        return promise.daysOverdue && promise.daysOverdue > 0 ? (
          <span className="text-red-600 font-medium">{promise.daysOverdue}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'currency':
        return <span className="text-sm">{promise.currencyCode || baseCurrency}</span>;
      case 'amount': {
        const displayAmount = currencyMode === 'base' ? (promise.basePromiseAmount ?? 0) : promise.promisedAmount;
        const displayCurrency = currencyMode === 'base' ? baseCurrency : promise.currencyCode || baseCurrency;
        return <div className="font-medium">{formatCurrency(displayAmount, displayCurrency)}</div>;
      }
      case 'transactions':
        return <div className="text-sm">{promise.invoiceCount ?? 0}</div>;
      case 'status':
        return getStatusBadge(promise.status);
      case 'method':
        return getConfirmationMethodBadge(promise.confirmationMethod);
      case 'customerEmail':
        return <span className="text-sm">{promise.customerEmail || '-'}</span>;
      case 'customerPhone':
        return <span className="text-sm">{promise.customerPhone || '-'}</span>;
      case 'createdDate':
        return (
          <span className="text-sm">
            {promise.createdAt ? formatDateDDMMYYYY(promise.createdAt.toISOString()) : '-'}
          </span>
        );
      case 'createdBy':
        return <span className="text-sm">{promise.createdBy || '-'}</span>;
      case 'assignedUser':
        return <span className="text-sm">{promise.assignedUserName || '-'}</span>;
      case 'notes':
        return <span className="text-sm truncate max-w-xs block">{promise.notes || '-'}</span>;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6 h-full min-h-[600px]">
      {/* Header */}
      <div className="shrink-0 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <HandCoins className="h-8 w-8" />
            Promise to Pay Management
          </h1>
          <p className="text-muted-foreground">Track customer payment commitments and monitor promise fulfillment</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleCreateNewPromise} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Create New Promise
          </Button>
          <HelpSupportButton screenId="promises" />
        </div>
      </div>

      {/* KPI Cards - display only, values update based on filters */}
      <div className="shrink-0 grid gap-4 md:grid-cols-3">
        <PromiseKPICard
          title="Active Promises"
          value={kpis.activePromises.count}
          subtitle={`${formatCurrency(kpis.activePromises.totalValue, activeCurrencyFilter)} total value`}
          icon={CheckCircle2}
          colorScheme="green"
          isLoading={isLoading}
          activeCurrencyFilter={activeCurrencyFilter}
          baseCurrency={baseCurrency}
        />
        <PromiseKPICard
          title="Broken Promises"
          value={kpis.brokenPromises.count}
          subtitle={`${formatCurrency(kpis.brokenPromises.totalValue, activeCurrencyFilter)} at risk`}
          icon={AlertCircle}
          colorScheme="red"
          isLoading={isLoading}
          activeCurrencyFilter={activeCurrencyFilter}
          baseCurrency={baseCurrency}
        />
        <PromiseKPICard
          title="Due This Week"
          value={kpis.dueThisWeek.count}
          subtitle={`${formatCurrency(kpis.dueThisWeek.totalValue, activeCurrencyFilter)} expected`}
          icon={Clock}
          colorScheme="amber"
          isLoading={isLoading}
          activeCurrencyFilter={activeCurrencyFilter}
          baseCurrency={baseCurrency}
        />
      </div>

      {/* Table Section */}
      <div className="flex flex-col flex-1 min-h-0">
        {/* Sticky toolbar */}
        <div className="sticky top-0 z-20 bg-background border rounded-t-xl px-4 py-3">
          <div className="flex items-center gap-4">
            <span className="text-lg font-bold text-gray-900 whitespace-nowrap">
              Promises ({pagination.totalItems})
            </span>
            <div className="relative flex-1 min-w-[200px]">
              {!searchQuery && (
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              )}
              <Input
                placeholder="Search by name, number, or case..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className={searchQuery ? 'pr-9' : 'pl-10'}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setCurrentPage(1);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <CurrencyToggle
              value={currencyMode}
              onChange={setCurrencyMode}
              nonBaseLabel="Promise Currency"
              baseLabel={`Base Currency (${baseCurrency})`}
            />
            <Button variant="outline" onClick={handleOpenMoreFilters}>
              <Filter className="mr-2 h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <ArrowDownToLine className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportToExcel}>Export Current View</DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportAllColumns}>Export All Columns</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => setColumnConfigOpen(true)} className="h-8 w-8">
                    <Settings className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Configure columns</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {activeFilterCount === 0 && !searchQuery && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-blue-50 px-3 py-1.5 rounded-md border border-blue-200 mt-2">
              <Filter className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-blue-700">
                Default filter active: <span className="font-medium">Excluding Paid and Closed</span>
              </span>
              <button
                onClick={() => {
                  setAdvancedFilters({ ...advancedFilters, statuses: [] });
                  toast.info('Default filter removed');
                }}
                className="ml-1 text-blue-600 hover:text-blue-800 transition-colors"
                title="Remove default filter"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Table card */}
        <Card className="rounded-t-none border-t-0 flex flex-col flex-1 min-h-0">
          <CardContent className="p-0 flex flex-col flex-1 min-h-0">
            {/* Scrollable table area */}
            <div className="flex-1 min-h-[200px] overflow-auto">
              <table className="w-full caption-bottom text-sm">
                <thead className="sticky top-0 z-10 bg-white shadow-sm">
                  <tr>
                    <th className="h-10 text-center font-bold text-gray-700 w-[40px] min-w-[40px] max-w-[40px] border-r border-gray-200">
                      <div className="flex items-center justify-center h-full">
                        <Checkbox
                          checked={isAllSelected}
                          onCheckedChange={handleSelectAll}
                          aria-label="Select all promises"
                          className={cn(isSomeSelected && !isAllSelected && 'data-[state=checked]:bg-primary')}
                        />
                      </div>
                    </th>
                    {visibleColumns
                      .filter((colId) => colId !== 'select')
                      .map((columnId) => {
                        const baseHeader = COLUMN_HEADERS[columnId] ?? { label: columnId };
                        const header =
                          columnId === 'amount' && currencyMode === 'base'
                            ? { ...baseHeader, label: `Amount (${baseCurrency})` }
                            : baseHeader;
                        const isSortable = SORTABLE_COLUMNS.includes(columnId);
                        const colWidth = localColumnWidths[columnId];
                        const isDragOver = dragOverColumn === columnId && draggedColumn !== columnId;
                        const isPinLeft = pinnedLeft.includes(columnId);
                        const isPinRight = pinnedRight.includes(columnId);
                        const pinLeftOffset = pinnedLeft.indexOf(columnId) * (colWidth || 150);
                        const pinRightOffset = pinnedRight.indexOf(columnId) * (colWidth || 150);
                        return (
                          <th
                            key={columnId}
                            draggable
                            onDragStart={() => setDraggedColumn(columnId)}
                            onDragOver={(e) => {
                              e.preventDefault();
                              setDragOverColumn(columnId);
                            }}
                            onDragEnd={() => {
                              setDraggedColumn(null);
                              setDragOverColumn(null);
                            }}
                            onDrop={() => {
                              if (!draggedColumn || draggedColumn === columnId) return;
                              const newCols = [...visibleColumns];
                              const fromIdx = newCols.indexOf(draggedColumn);
                              const toIdx = newCols.indexOf(columnId);
                              newCols.splice(fromIdx, 1);
                              newCols.splice(toIdx, 0, draggedColumn);
                              setColumnOrder(newCols);
                              setDraggedColumn(null);
                              setDragOverColumn(null);
                            }}
                            style={{
                              width: colWidth ? `${colWidth}px` : undefined,
                              minWidth: colWidth ? `${colWidth}px` : undefined,
                              position: isPinLeft || isPinRight ? 'sticky' : 'relative',
                              left: isPinLeft ? `${pinLeftOffset}px` : undefined,
                              right: isPinRight ? `${pinRightOffset}px` : undefined,
                              zIndex: isPinLeft || isPinRight ? 20 : undefined,
                            }}
                            className={cn(
                              'h-10 px-4 text-left font-bold text-gray-700 border-r border-gray-200 last:border-r-0 select-none bg-white',
                              isDragOver && 'bg-blue-50 border-l-2 border-l-blue-400',
                              draggedColumn === columnId && 'opacity-50',
                              (isPinLeft || isPinRight) && 'shadow-md',
                              header.className,
                            )}
                          >
                            <div className="flex items-center justify-between gap-1 w-full">
                              {isSortable ? (
                                <button
                                  onClick={() => handleSort(columnId as SortField)}
                                  className="flex items-center gap-1 hover:text-gray-900 transition-colors font-bold"
                                >
                                  <span>{header.label}</span>
                                  <span className="text-gray-500">{getSortIcon(columnId as SortField)}</span>
                                </button>
                              ) : (
                                <span className="font-bold">{header.label}</span>
                              )}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <button className="p-0.5 rounded hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0">
                                    <MoreVertical className="h-4 w-4 text-gray-300 hover:text-gray-500" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="z-[9999] w-48">
                                  <DropdownMenuItem onClick={() => handlePinLeft(columnId)}>
                                    <Pin className="h-4 w-4 mr-2" />
                                    {isPinLeft ? 'Unpin from Left' : 'Pin to Left'}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handlePinRight(columnId)}>
                                    <Pin className="h-4 w-4 mr-2 rotate-180" />
                                    {isPinRight ? 'Unpin from Right' : 'Pin to Right'}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={handleResetColumnWidths}>
                                    <Maximize2 className="h-4 w-4 mr-2" />
                                    Reset Column Widths
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={handleResetColumnOrder}>
                                    <RotateCcw className="h-4 w-4 mr-2" />
                                    Reset Column Order
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                            {/* Resize handle */}
                            <button
                              type="button"
                              aria-label="Resize column"
                              className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-blue-300 opacity-0 hover:opacity-60 transition-opacity z-10 p-0 border-0 bg-transparent"
                              onMouseDown={(e) =>
                                handleResizeMouseDown(
                                  e,
                                  columnId,
                                  colWidth || (e.currentTarget.closest('th') as HTMLElement)?.offsetWidth || 150,
                                )
                              }
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                          </th>
                        );
                      })}
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td colSpan={visibleColumns.length + 1} className="h-64 text-center">
                        <div className="flex flex-col items-center justify-center space-y-4">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          <p className="text-sm text-muted-foreground">Loading promises...</p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!isLoading && promises.length === 0 && (
                    <tr>
                      <td colSpan={visibleColumns.length + 1} className="h-64 text-center">
                        <p className="text-sm text-muted-foreground">No promises found matching your criteria</p>
                      </td>
                    </tr>
                  )}
                  {!isLoading &&
                    promises.length > 0 &&
                    promises.map((promise, index) => (
                      <tr
                        key={promise.id}
                        className={cn(
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                          selectedPromiseIds.has(promise.id) && 'bg-muted/50',
                          'hover:bg-muted/30 transition-colors',
                        )}
                      >
                        <td className="h-10 text-center w-[40px] min-w-[40px] max-w-[40px] border-r border-gray-100">
                          <div className="flex items-center justify-center h-full">
                            <Checkbox
                              checked={selectedPromiseIds.has(promise.id)}
                              onCheckedChange={(checked) => handleSelectPromise(promise.id, checked as boolean)}
                              aria-label={`Select promise ${promise.id}`}
                            />
                          </div>
                        </td>
                        {visibleColumns
                          .filter((colId) => colId !== 'select')
                          .map((colId) => {
                            const colWidth = localColumnWidths[colId];
                            const isPinLeft = pinnedLeft.includes(colId);
                            const isPinRight = pinnedRight.includes(colId);
                            const pinLeftOffset = pinnedLeft.indexOf(colId) * (colWidth || 150);
                            const pinRightOffset = pinnedRight.indexOf(colId) * (colWidth || 150);
                            return (
                              <td
                                key={colId}
                                style={{
                                  width: colWidth ? `${colWidth}px` : undefined,
                                  minWidth: colWidth ? `${colWidth}px` : undefined,
                                  position: isPinLeft || isPinRight ? 'sticky' : undefined,
                                  left: isPinLeft ? `${pinLeftOffset}px` : undefined,
                                  right: isPinRight ? `${pinRightOffset}px` : undefined,
                                  zIndex: isPinLeft || isPinRight ? 10 : undefined,
                                }}
                                className={cn(
                                  'px-4 py-2 align-middle border-r border-gray-100 last:border-r-0',
                                  (isPinLeft || isPinRight) && 'shadow-md',
                                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                                )}
                              >
                                {renderCellContent(colId, promise)}
                              </td>
                            );
                          })}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            <div className="shrink-0 border-t px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {pageNumbers.map((page) =>
                    typeof page === 'string' ? (
                      <span key={page} className="px-1 text-muted-foreground">
                        &hellip;
                      </span>
                    ) : (
                      <Button
                        key={`page-${page}`}
                        variant={currentPage === page ? 'default' : 'outline'}
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Button>
                    ),
                  )}
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Drawer (Sheet) */}
      <Sheet open={moreFiltersOpen} onOpenChange={handleCloseMoreFilters}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-hidden flex flex-col h-full">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
            <SheetDescription>Filter your promises by status, assigned user, and more.</SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-4 mt-6">
            <div className="space-y-4 pb-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Status</Label>
                <div className="space-y-3">
                  {STATUS_OPTIONS.map((status) => (
                    <label key={status} className="flex items-center gap-3 cursor-pointer">
                      <Checkbox
                        checked={draftFilters.statuses.includes(status)}
                        onCheckedChange={(checked) => {
                          const updatedStatuses = checked
                            ? [...draftFilters.statuses, status]
                            : draftFilters.statuses.filter((s) => s !== status);
                          setDraftFilters((prev) => ({ ...prev, statuses: updatedStatuses }));
                        }}
                      />
                      <span className="text-sm">{status}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Assigned User</Label>
                <Select
                  value={draftFilters.assignedUser}
                  onValueChange={(value) => setDraftFilters((prev) => ({ ...prev, assignedUser: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {userFilterOptions.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Currency</Label>
                <MultiSelect
                  options={currencyFilterOptions}
                  placeholder="Select currencies..."
                  emptyMessage="No currencies found."
                  value={draftFilters.currencies}
                  onValueChange={(values) => setDraftFilters((prev) => ({ ...prev, currencies: values }))}
                />
              </div>

              <div className="border-t pt-4 mt-4">
                <Label className="text-sm font-semibold mb-3 block">Created At</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">From</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full justify-start text-left font-normal',
                            !draftFilters.createdAtFrom && 'text-muted-foreground',
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {draftFilters.createdAtFrom
                            ? formatDate(new Date(draftFilters.createdAtFrom + 'T00:00:00'))
                            : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={
                            draftFilters.createdAtFrom ? new Date(draftFilters.createdAtFrom + 'T00:00:00') : undefined
                          }
                          onSelect={(date) =>
                            setDraftFilters((prev) => ({
                              ...prev,
                              createdAtFrom: date ? format(date, 'yyyy-MM-dd') : '',
                            }))
                          }
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">To</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full justify-start text-left font-normal',
                            !draftFilters.createdAtTo && 'text-muted-foreground',
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {draftFilters.createdAtTo
                            ? formatDate(new Date(draftFilters.createdAtTo + 'T00:00:00'))
                            : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={
                            draftFilters.createdAtTo ? new Date(draftFilters.createdAtTo + 'T00:00:00') : undefined
                          }
                          onSelect={(date) =>
                            setDraftFilters((prev) => ({
                              ...prev,
                              createdAtTo: date ? format(date, 'yyyy-MM-dd') : '',
                            }))
                          }
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <Label className="text-sm font-semibold mb-3 block">Quick Filters</Label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={draftFilters.dueToday}
                      onCheckedChange={(checked) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          dueToday: !!checked,
                        }))
                      }
                    />
                    <span className="text-sm">Due Today</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <SheetFooter className="p-6 bg-background border-t">
            <div className="flex gap-2 w-full">
              <Button variant="outline" className="flex-1" onClick={handleClearDraftFilters}>
                Clear All
              </Button>
              <Button className="flex-1" onClick={handleApplyFilters}>
                Apply Filters
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Email Modal */}
      <PromiseSendEmailModal
        open={emailModalOpen}
        onOpenChange={setEmailModalOpen}
        promiseId={selectedPromiseForModal?.id || ''}
        customerId={selectedPromiseForModal?.customerId || ''}
        customerName={selectedPromiseForModal?.customerName || ''}
        accountNumber={selectedPromiseForModal?.customerAccountNumber || ''}
      />

      {/* Call Log Modal */}
      <LogCallModal
        open={callModalOpen}
        onOpenChange={setCallModalOpen}
        customerName={selectedPromiseForModal?.customerName || ''}
        customerPhone={selectedPromiseForModal?.customerPhone}
        promiseId={selectedPromiseForModal?.id}
      />

      {/* Reassign User Modal */}
      <ReassignUserModal
        open={reassignUserModalOpen}
        onOpenChange={setReassignUserModalOpen}
        selectedCount={selectedPromiseIds.size}
        onSave={handleReassignUserSave}
        users={reassignUserOptions}
        isLoadingUsers={isLoadingUsers}
      />

      {/* Update Status Modal */}
      <UpdateStatusModal
        open={updateStatusModalOpen}
        onOpenChange={setUpdateStatusModalOpen}
        selectedCount={selectedPromiseIds.size}
        onSave={handleUpdateStatusSave}
      />

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onApply={savePreferences}
        onReset={resetToDefaults}
      />

      {/* Floating Actions Bar */}
      {selectedPromiseIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
          <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
            <div className="flex items-center gap-4 px-6 py-4">
              {/* Selection Info */}
              <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30"
                    >
                      {selectedPromiseIds.size}
                    </Badge>
                    <span className="font-medium">
                      {selectedPromiseIds.size === 1 ? 'promise' : 'promises'} selected
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {selectedPromiseIds.size === 1 ? (
                  <>
                    <Button variant="secondary" size="sm" onClick={handleBulkEmail} className="gap-2">
                      <Mail className="h-4 w-4" />
                      Send Email
                    </Button>
                    <Button variant="secondary" size="sm" onClick={handleBulkCall} className="gap-2">
                      <Phone className="h-4 w-4" />
                      Log Call
                    </Button>
                    <Button variant="secondary" size="sm" onClick={handleBulkReassignUser} className="gap-2">
                      <UserCog className="h-4 w-4" />
                      Reassign User
                    </Button>
                    <Button variant="secondary" size="sm" onClick={handleBulkUpdateStatus} className="gap-2">
                      <RefreshCw className="h-4 w-4" />
                      Update Status
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="secondary" size="sm" onClick={handleBulkReassignUser} className="gap-2">
                      <UserCog className="h-4 w-4" />
                      Reassign User
                    </Button>
                    <Button variant="secondary" size="sm" onClick={handleBulkUpdateStatus} className="gap-2">
                      <RefreshCw className="h-4 w-4" />
                      Update Status
                    </Button>
                  </>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  className="gap-2 hover:bg-primary-foreground/20"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
